import { useState, useEffect } from 'react'
import BrandLogo from './BrandLogo'
import { useT } from '@/i18n'
import { useAuth } from '@/auth'
import { useTheme } from '@/theme'

export default function SiteNav({ glass = false }: { glass?: boolean } = {}) {
  const t = useT()
  const { user, startGoogleLogin, logout } = useAuth()
  const { skyMode, toggleSky, showPhoto, togglePhoto, fadeStart, setFadeStart, fadeEnd, setFadeEnd } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [fadeOpen, setFadeOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  const path = window.location.pathname
  // Map-mode (navigate-tile passes `glass`): on mobile the header bar collapses
  // to just the logo + title, and tapping the logo opens the menu (#87).
  const mapMode = glass

  // Glassmorphism on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Track mobile breakpoint (drives map-mode collapse)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = () => setIsMobile(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Close menu on Escape
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const isActive = (href: string) => path === href

  const navLinks = [
    { href: '/', label: t.nav.home, icon: 'home' },
    { href: '/adventure', label: 'Abenteuer', icon: 'compass' },
    { href: '/browse', label: t.nav.floraFauna, icon: 'leaf' },
    { href: '/routes', label: t.nav.routes, icon: 'route' },
    { href: '/stories', label: t.nav.stories, icon: 'book' },
    { href: '/submit', label: 'Beitrag senden', icon: 'camera' },
    { href: '/navigate-mapbox', label: 'Karte (Mapbox)', icon: 'compass' },
    { href: '/navigate-tile', label: 'Karte (Tile)', icon: 'compass' },
  ]

  // Die Landing ist die einzige Seite, deren transparenter Header über dem
  // DUNKLEN Hero-Video liegt. Dort dürfen die --light-Farben (schwarzes Logo,
  // dunkle Links) nicht greifen, solange kein Glass-Hintergrund darunter
  // liegt — sonst steht schwarz auf dunklem Fels (Alex 2026-07-27).
  // Die Landing (Hero-Video) wohnt seit 2026-08-10 unter /landing — die
  // Wurzel ist die Karte selbst.
  const overHero = path === '/landing'

  const headerClass = [
    'sn-header',
    glass ? 'sn-header--glass' : (!scrolled && !menuOpen ? 'sn-header--transparent' : ''),
    mapMode ? 'sn-header--map' : '',
    menuOpen ? 'sn-header--menu-open' : '',
    skyMode === 'light' ? 'sn-header--light' : '',
    overHero ? 'sn-header--hero' : '',
  ].filter(Boolean).join(' ')

  return (
    <>
      <header className={headerClass}>
        <div className="sn-header__inner">
          {/* Logo — in map-mode on mobile it opens the menu instead of linking home (#87) */}
          {mapMode && isMobile ? (
            /* Auf der Karte NUR die Bildmarke — gemessen (29.07.): die
               geschlossene Marke ist 140px breit, und ab x≈150 sitzt der
               Guide-Kopf. Die Wortmarke lief sichtbar hinter dem Kopf
               durch, „PP" ragte rechts wieder heraus. Genau deshalb stand
               hier schon vorher der Name nicht. Der Tropfen ist derselbe
               wie in der geschlossenen Marke, die Herkunft bleibt lesbar. */
            <button type="button" className="sn-header__brand sn-header__brand--btn" onClick={() => setMenuOpen(true)} aria-label="Menü öffnen">
              <BrandLogo mark="color" markClassName="sn-header__logo" nameClassName="sn-header__title" style={{ gap: 'inherit' }} />
            </button>
          ) : (
            <a href="/" className="sn-header__brand">
              <BrandLogo lockup markClassName="sn-header__lockup" />
            </a>
          )}

          {/* Desktop nav links */}
          <nav className="sn-header__links">
            {navLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                className={`sn-header__link ${isActive(link.href) ? 'sn-header__link--active' : ''}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side: theme toggles + auth + hamburger */}
          <div className="sn-header__actions">
            {/* Sky mode toggle */}
            <button className="sn-header__icon-btn" onClick={toggleSky} aria-label="Light / Dark">
              {skyMode === 'night' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>

            {/* Photo background toggle */}
            <button className={`sn-header__icon-btn${showPhoto ? ' sn-header__icon-btn--active' : ''}`} onClick={togglePhoto} aria-label="Photo Background">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
              </svg>
            </button>

            {/* Fade sliders — only when photo is active */}
            {showPhoto && (
              <div className="sn-fade-wrap">
                <button className={`sn-header__icon-btn${fadeOpen ? ' sn-header__icon-btn--active' : ''}`} onClick={() => setFadeOpen(!fadeOpen)} aria-label="Fade Settings">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                  </svg>
                </button>
                {fadeOpen && (
                  <div className="sn-fade-panel">
                    <label>
                      <span>Start {fadeStart}%</span>
                      <input type="range" min={0} max={100} value={fadeStart} onChange={e => setFadeStart(Number(e.target.value))} />
                    </label>
                    <label>
                      <span>Transparent {fadeEnd}%</span>
                      <input type="range" min={0} max={100} value={fadeEnd} onChange={e => setFadeEnd(Number(e.target.value))} />
                    </label>
                  </div>
                )}
              </div>
            )}

            {user ? (
              <button className="sn-header__user" onClick={logout} title={t.nav.logout}>
                {user.first_name}
              </button>
            ) : (
              <button className="sn-header__auth-btn" onClick={() => { void startGoogleLogin() }}>
                {t.nav.login}
              </button>
            )}
            <button
              className={`sn-header__burger ${menuOpen ? 'sn-header__burger--open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      <div className={`sn-drawer ${menuOpen ? 'sn-drawer--open' : ''}`}>
        <div className="sn-drawer__backdrop" onClick={() => setMenuOpen(false)} />
        <div className="sn-drawer__panel">
          <nav className="sn-drawer__nav">
            {navLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                className={`sn-drawer__link ${isActive(link.href) ? 'sn-drawer__link--active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                <NavIcon name={link.icon} />
                {link.label}
              </a>
            ))}
          </nav>

          <div className="sn-drawer__divider" />

          {/* Theme toggles in drawer */}
          <div className="sn-drawer__theme">
            <button className="sn-drawer__theme-btn" onClick={toggleSky}>
              {skyMode === 'night' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {skyMode === 'night' ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button className={`sn-drawer__theme-btn${showPhoto ? ' sn-drawer__theme-btn--active' : ''}`} onClick={togglePhoto}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M3 16l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
              </svg>
              Photo Background
            </button>
          </div>

          <div className="sn-drawer__divider" />

          {/* Auth section */}
          <div className="sn-drawer__auth">
            {user ? (
              <>
                <div className="sn-drawer__user">
                  <div className="sn-drawer__avatar">{user.first_name[0]}</div>
                  <div>
                    <div className="sn-drawer__user-name">{user.first_name} {user.last_name}</div>
                    <div className="sn-drawer__user-email">{user.email}</div>
                  </div>
                </div>
                <button className="sn-drawer__logout" onClick={() => { logout(); setMenuOpen(false) }}>
                  {t.nav.logout}
                </button>
              </>
            ) : (
              <button className="sn-drawer__login" onClick={() => { startGoogleLogin(); setMenuOpen(false) }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {t.nav.login}
              </button>
            )}
          </div>
        </div>
      </div>

    </>
  )
}

function NavIcon({ name }: { name: string }) {
  switch (name) {
    case 'home':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" /></svg>
    case 'leaf':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" /></svg>
    case 'route':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 000-7h-11a3.5 3.5 0 010-7H15" /><circle cx="18" cy="5" r="3" /></svg>
    case 'book':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></svg>
    case 'compass':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity="0.3" stroke="currentColor" /></svg>
    case 'camera':
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
    default:
      return null
  }
}
